import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Store,
  ArrowLeft,
  Save,
  Upload,
  CreditCard,
  Building,
  Mail,
  Phone,
  MapPin,
  FileText,
  CheckCircle,
  AlertCircle,
  ExternalLink,
  Wallet,
  Receipt
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function SellerSettings() {
  const { token } = useAuth();
  const [seller, setSeller] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    company_name: "",
    description: "",
    contact_email: "",
    contact_phone: "",
    org_number: "",
    logo_url: "",
    banner_url: "",
    address: {
      street: "",
      city: "",
      postal_code: "",
      country: "Norge"
    },
    payment_method: "invoice", // "stripe", "invoice", "both"
    bank_account: {
      account_number: "",
      bank_name: ""
    }
  });

  const fetchSeller = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/sellers/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSeller(res.data);
      setFormData({
        company_name: res.data.company_name || "",
        description: res.data.description || "",
        contact_email: res.data.contact_email || "",
        contact_phone: res.data.contact_phone || "",
        org_number: res.data.org_number || "",
        logo_url: res.data.logo_url || "",
        banner_url: res.data.banner_url || "",
        address: res.data.address || { street: "", city: "", postal_code: "", country: "Norge" },
        payment_method: res.data.payment_method || "invoice",
        bank_account: res.data.bank_account || { account_number: "", bank_name: "" }
      });
    } catch (err) {
      toast.error("Kunne ikke laste data");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchSeller();
  }, [fetchSeller]);

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formDataUpload = new FormData();
    formDataUpload.append("file", file);

    try {
      const res = await axios.post(`${API}/api/upload`, formDataUpload, {
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });
      setFormData(prev => ({ ...prev, logo_url: res.data.url }));
      toast.success("Logo lastet opp!");
    } catch (err) {
      toast.error("Kunne ikke laste opp logo");
    }
  };

  const handleBannerUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formDataUpload = new FormData();
    formDataUpload.append("file", file);

    try {
      const res = await axios.post(`${API}/api/upload`, formDataUpload, {
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });
      setFormData(prev => ({ ...prev, banner_url: res.data.url }));
      toast.success("Banner lastet opp!");
    } catch (err) {
      toast.error("Kunne ikke laste opp banner");
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/sellers/me`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Innstillinger lagret!");
      fetchSeller();
    } catch (err) {
      toast.error("Kunne ikke lagre innstillinger");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#23F7DD] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] py-8 px-4">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Link to="/seller/dashboard">
            <Button variant="ghost" size="icon" className="text-[#94A3B8] hover:text-white">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-['Unbounded'] font-bold text-white">Innstillinger</h1>
            <p className="text-[#94A3B8]">{seller?.seller_code}</p>
          </div>
        </div>

        {/* Profile Card */}
        <Card className="bg-[#0A0A0A] border-white/10">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Store className="w-5 h-5 text-[#F7931A]" />
              Butikkprofil
            </CardTitle>
            <CardDescription className="text-[#94A3B8]">
              Denne informasjonen vises til kundene på din butikkside
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Logo & Banner */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="text-sm text-[#94A3B8] mb-2 block">Logo</label>
                <div className="flex items-center gap-4">
                  {formData.logo_url ? (
                    <img 
                      src={formData.logo_url} 
                      alt="Logo" 
                      className="w-20 h-20 rounded-xl object-cover border border-white/10"
                    />
                  ) : (
                    <div className="w-20 h-20 rounded-xl bg-[#F7931A]/20 flex items-center justify-center">
                      <Store className="w-8 h-8 text-[#F7931A]" />
                    </div>
                  )}
                  <label className="cursor-pointer">
                    <Button variant="outline" className="border-white/20 text-white" asChild>
                      <span>
                        <Upload className="w-4 h-4 mr-2" />
                        Last opp
                      </span>
                    </Button>
                    <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" />
                  </label>
                </div>
              </div>
              
              <div>
                <label className="text-sm text-[#94A3B8] mb-2 block">Banner</label>
                <div className="relative">
                  {formData.banner_url ? (
                    <img 
                      src={formData.banner_url} 
                      alt="Banner" 
                      className="w-full h-24 rounded-lg object-cover border border-white/10"
                    />
                  ) : (
                    <div className="w-full h-24 rounded-lg bg-white/5 flex items-center justify-center border border-dashed border-white/20">
                      <span className="text-[#94A3B8] text-sm">Ingen banner</span>
                    </div>
                  )}
                  <label className="absolute bottom-2 right-2 cursor-pointer">
                    <Button size="sm" variant="outline" className="border-white/20 text-white bg-black/50" asChild>
                      <span>
                        <Upload className="w-3 h-3 mr-1" />
                        Endre
                      </span>
                    </Button>
                    <input type="file" accept="image/*" onChange={handleBannerUpload} className="hidden" />
                  </label>
                </div>
              </div>
            </div>

            {/* Company Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-[#94A3B8] mb-1 block flex items-center gap-2">
                  <Building className="w-3 h-3" /> Firmanavn
                </label>
                <Input
                  value={formData.company_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, company_name: e.target.value }))}
                  className="bg-black/50 border-white/10 text-white"
                />
              </div>
              <div>
                <label className="text-sm text-[#94A3B8] mb-1 block">Org.nummer</label>
                <Input
                  value={formData.org_number}
                  onChange={(e) => setFormData(prev => ({ ...prev, org_number: e.target.value }))}
                  className="bg-black/50 border-white/10 text-white"
                />
              </div>
            </div>

            {/* Contact */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-[#94A3B8] mb-1 block flex items-center gap-2">
                  <Mail className="w-3 h-3" /> E-post
                </label>
                <Input
                  type="email"
                  value={formData.contact_email}
                  onChange={(e) => setFormData(prev => ({ ...prev, contact_email: e.target.value }))}
                  className="bg-black/50 border-white/10 text-white"
                />
              </div>
              <div>
                <label className="text-sm text-[#94A3B8] mb-1 block flex items-center gap-2">
                  <Phone className="w-3 h-3" /> Telefon
                </label>
                <Input
                  value={formData.contact_phone}
                  onChange={(e) => setFormData(prev => ({ ...prev, contact_phone: e.target.value }))}
                  className="bg-black/50 border-white/10 text-white"
                />
              </div>
            </div>

            {/* Address */}
            <div>
              <label className="text-sm text-[#94A3B8] mb-2 block flex items-center gap-2">
                <MapPin className="w-3 h-3" /> Adresse
              </label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Input
                  value={formData.address.street}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    address: { ...prev.address, street: e.target.value }
                  }))}
                  placeholder="Gateadresse"
                  className="bg-black/50 border-white/10 text-white md:col-span-2"
                />
                <div className="flex gap-2">
                  <Input
                    value={formData.address.postal_code}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      address: { ...prev.address, postal_code: e.target.value }
                    }))}
                    placeholder="Postnr"
                    className="bg-black/50 border-white/10 text-white w-24"
                  />
                  <Input
                    value={formData.address.city}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      address: { ...prev.address, city: e.target.value }
                    }))}
                    placeholder="Sted"
                    className="bg-black/50 border-white/10 text-white flex-1"
                  />
                </div>
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="text-sm text-[#94A3B8] mb-1 block flex items-center gap-2">
                <FileText className="w-3 h-3" /> Om butikken
              </label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Beskriv din butikk og hva du selger..."
                className="bg-black/50 border-white/10 text-white"
                rows={3}
              />
            </div>
          </CardContent>
        </Card>

        {/* Payment Settings */}
        <Card className="bg-[#0A0A0A] border-white/10">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Wallet className="w-5 h-5 text-green-400" />
              Betalingsinnstillinger
            </CardTitle>
            <CardDescription className="text-[#94A3B8]">
              Velg hvordan du vil motta utbetalinger for dine salg
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Payment Method Selection */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Stripe Option */}
              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, payment_method: "stripe" }))}
                className={`p-4 rounded-lg border-2 text-left transition-all ${
                  formData.payment_method === "stripe"
                    ? "border-[#23F7DD] bg-[#23F7DD]/10"
                    : "border-white/20 hover:border-white/40"
                }`}
              >
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 bg-[#635BFF]/20 rounded-lg flex items-center justify-center">
                    <CreditCard className="w-5 h-5 text-[#635BFF]" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold">Stripe Connect</h3>
                    <p className="text-xs text-[#94A3B8]">Automatiske utbetalinger</p>
                  </div>
                </div>
                <ul className="text-sm text-[#94A3B8] space-y-1 ml-13">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-3 h-3 text-green-400" />
                    Betal direkte til din konto
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-3 h-3 text-green-400" />
                    Automatisk etter hver ordre
                  </li>
                  <li className="flex items-center gap-2">
                    <AlertCircle className="w-3 h-3 text-yellow-400" />
                    Krever Stripe-konto
                  </li>
                </ul>
              </button>

              {/* Invoice/Manual Option */}
              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, payment_method: "invoice" }))}
                className={`p-4 rounded-lg border-2 text-left transition-all ${
                  formData.payment_method === "invoice"
                    ? "border-[#23F7DD] bg-[#23F7DD]/10"
                    : "border-white/20 hover:border-white/40"
                }`}
              >
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 bg-[#F7931A]/20 rounded-lg flex items-center justify-center">
                    <Receipt className="w-5 h-5 text-[#F7931A]" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold">Faktura / Overføring</h3>
                    <p className="text-xs text-[#94A3B8]">Månedlige utbetalinger</p>
                  </div>
                </div>
                <ul className="text-sm text-[#94A3B8] space-y-1 ml-13">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-3 h-3 text-green-400" />
                    Ingen Stripe-konto nødvendig
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-3 h-3 text-green-400" />
                    Utbetaling en gang i måneden
                  </li>
                  <li className="flex items-center gap-2">
                    <AlertCircle className="w-3 h-3 text-yellow-400" />
                    Du sender faktura til AllStillas
                  </li>
                </ul>
              </button>
            </div>

            {/* Stripe Connect Setup */}
            {formData.payment_method === "stripe" && (
              <div className="p-4 bg-[#635BFF]/10 border border-[#635BFF]/30 rounded-lg">
                {seller?.stripe_onboarded ? (
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-6 h-6 text-green-400" />
                    <div>
                      <p className="text-white font-medium">Stripe Connect er aktivert!</p>
                      <p className="text-sm text-[#94A3B8]">Du mottar automatiske utbetalinger</p>
                    </div>
                  </div>
                ) : (
                  <div>
                    <p className="text-white mb-3">
                      For å motta automatiske utbetalinger må du koble til Stripe-kontoen din:
                    </p>
                    <Button className="bg-[#635BFF] text-white hover:bg-[#635BFF]/80">
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Koble til Stripe
                    </Button>
                    <p className="text-xs text-[#94A3B8] mt-2">
                      Du vil bli sendt til Stripe for å fullføre registreringen
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Bank Account for Invoice */}
            {formData.payment_method === "invoice" && (
              <div className="space-y-4">
                <p className="text-[#94A3B8] text-sm">
                  Oppgi bankkonto for månedlige utbetalinger. AllStillas sender utbetalingsrapport 
                  ved slutten av hver måned, og du sender faktura basert på rapporten.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-[#94A3B8] mb-1 block">Kontonummer</label>
                    <Input
                      value={formData.bank_account.account_number}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        bank_account: { ...prev.bank_account, account_number: e.target.value }
                      }))}
                      placeholder="1234 56 78901"
                      className="bg-black/50 border-white/10 text-white"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-[#94A3B8] mb-1 block">Bank</label>
                    <Input
                      value={formData.bank_account.bank_name}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        bank_account: { ...prev.bank_account, bank_name: e.target.value }
                      }))}
                      placeholder="DNB, Nordea, etc."
                      className="bg-black/50 border-white/10 text-white"
                    />
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Free Tier Info */}
        {seller?.free_tier?.in_free_tier && (
          <Card className="bg-gradient-to-r from-green-500/10 to-emerald-500/10 border-green-500/30">
            <CardContent className="p-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-green-400" />
                </div>
                <div>
                  <h3 className="text-white font-semibold">Gratis Tier Aktiv</h3>
                  <p className="text-[#94A3B8] text-sm">
                    0% provisjon fram til {seller.free_tier.threshold.toLocaleString()} NOK. 
                    Du har {seller.free_tier.remaining_free.toLocaleString()} NOK igjen.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Save Button */}
        <div className="flex justify-end">
          <Button
            onClick={saveSettings}
            disabled={saving}
            className="bg-[#F7931A] text-white hover:bg-[#F7931A]/80 px-8"
          >
            {saving ? (
              "Lagrer..."
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Lagre Innstillinger
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
